// src/components/Accounts/AccountsPage.js
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Alert,
  Snackbar,
  useMediaQuery,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AccountBalance as AccountBalanceIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useAccount } from '../../contexts/AccountContext';
import MultiCurrencyDashboard from '../Currency/MultiCurrencyDashboard';

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  'https://soldikeeper-backend-production.up.railway.app/api';

const createAuthApi = () => {
  const token = localStorage.getItem('token');
  if (!token) throw new Error('No authentication token found');
  return axios.create({
    baseURL: API_BASE_URL,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      'x-auth-token': token,
    },
  });
};

const ACCOUNT_TYPES = ['checking', 'savings', 'ewallet', 'cash', 'credit', 'investment', 'loan', 'mortgage', 'creditcard'];

const LIABILITY_TYPES = ['loan', 'mortgage', 'creditcard', 'credit'];

const TYPE_COLORS = {
  checking:   '#3b82f6',
  savings:    '#10b981',
  ewallet:    '#8b5cf6',
  cash:       '#f59e0b',
  credit:     '#ef4444',
  investment: '#06b6d4',
  loan:       '#f97316',
  mortgage:   '#dc2626',
  creditcard: '#be185d',
};

const EMPTY_FORM = {
  name: '',
  type: 'checking',
  initialBalance: '',
  currency: 'EUR',
  color: '#3b82f6',
  icon: '🏦',
  institution: '',
  interestRate: '',
  minimumPayment: '',
  originalAmount: '',
  dueDate: '',
};

// ─────────────────────────────────────────────────────
// AccountCard
// ─────────────────────────────────────────────────────
const AccountCard = ({ account, onEdit, onDelete, onSetDefault, t }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const allocated = account.allocated || 0;
  const available = account.available || 0;
  const current = account.currentBalance ?? account.initialBalance ?? 0;
  const total = Math.abs(current) || 1;
  const allocatedPct = Math.min(100, (allocated / total) * 100);

  return (
    <Card
      elevation={2}
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
        transition: 'box-shadow 0.2s, transform 0.2s',
        '&:hover': {
          boxShadow: 8,
          transform: 'translateY(-2px)',
        },
      }}
    >
      {/* Colour strip */}
      <Box sx={{ height: 6, bgcolor: account.color || '#3b82f6' }} />

      <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
        {/* Header row */}
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" mb={1.5}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Typography fontSize={28} lineHeight={1}>
              {account.icon || '🏦'}
            </Typography>
            <Box>
              <Typography variant="subtitle1" fontWeight={700} noWrap sx={{ maxWidth: 160 }}>
                {account.name}
              </Typography>
              <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
                <Chip
                  label={t(`accounts.types.${account.type}`, account.type)}
                  size="small"
                  sx={{
                    bgcolor: alpha(TYPE_COLORS[account.type] || '#3b82f6', isDark ? 0.25 : 0.12),
                    color: TYPE_COLORS[account.type] || '#3b82f6',
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    height: 20,
                    mt: 0.25,
                  }}
                />
                {account.isDefault && (
                  <Chip
                    label="Default"
                    size="small"
                    color="primary"
                    sx={{ fontSize: '0.7rem', height: 20, mt: 0.25 }}
                  />
                )}
              </Stack>
            </Box>
          </Stack>

          <Stack direction="row" spacing={0}>
            {!account.isDefault && (
              <Tooltip title="Set as Default">
                <IconButton size="small" onClick={() => onSetDefault(account._id)}>
                  <AccountBalanceIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={t('accounts.editAccount', 'Edit')}>
              <IconButton size="small" onClick={() => onEdit(account)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('accounts.deleteAccount', 'Delete')}>
              <IconButton size="small" color="error" onClick={() => onDelete(account)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        {/* Institution */}
        {account.institution && (
          <Typography variant="caption" color="text.secondary" display="block" mb={1}>
            {account.institution}
          </Typography>
        )}

        {/* Metrics */}
        <Stack spacing={0.75} mt={1}>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="caption" color="text.secondary">
              {t('accounts.currentBalance', 'Current Balance')}
            </Typography>
            <Typography variant="caption" fontWeight={700} color={current >= 0 ? 'success.main' : 'error.main'}>
              {current >= 0 ? '+' : ''}{current.toFixed(2)} {account.currency || 'EUR'}
            </Typography>
          </Stack>

          <Stack direction="row" justifyContent="space-between">
            <Typography variant="caption" color="text.secondary">
              {t('accounts.allocated', 'Allocated')}
            </Typography>
            <Typography variant="caption" fontWeight={600}>
              {allocated.toFixed(2)} {account.currency || 'EUR'}
            </Typography>
          </Stack>

          <Stack direction="row" justifyContent="space-between">
            <Typography variant="caption" color="text.secondary">
              {t('accounts.available', 'Available')}
            </Typography>
            <Typography variant="caption" fontWeight={600} color={available >= 0 ? 'text.primary' : 'error.main'}>
              {available.toFixed(2)} {account.currency || 'EUR'}
            </Typography>
          </Stack>
        </Stack>

        {/* Allocated progress */}
        {allocated > 0 && (
          <Box mt={1.5}>
            <LinearProgress
              variant="determinate"
              value={allocatedPct}
              sx={{
                height: 5,
                borderRadius: 3,
                bgcolor: alpha(theme.palette.divider, 0.3),
                '& .MuiLinearProgress-bar': {
                  bgcolor: account.color || '#3b82f6',
                  borderRadius: 3,
                },
              }}
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

// ─────────────────────────────────────────────────────
// AccountFormDialog
// ─────────────────────────────────────────────────────
const AccountFormDialog = ({ open, onClose, onSave, account, t }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (account) {
      setFormData({
        name: account.name || '',
        type: account.type || 'checking',
        initialBalance: account.initialBalance != null ? String(account.initialBalance) : '',
        currency: account.currency || 'EUR',
        color: account.color || '#3b82f6',
        icon: account.icon || '🏦',
        institution: account.institution || '',
        interestRate: account.interestRate != null ? String(account.interestRate) : '',
        minimumPayment: account.minimumPayment != null ? String(account.minimumPayment) : '',
        originalAmount: account.originalAmount != null ? String(account.originalAmount) : '',
        dueDate: account.dueDate != null ? String(account.dueDate) : '',
      });
    } else {
      setFormData(EMPTY_FORM);
    }
    setErrors({});
  }, [account, open]);

  const validate = () => {
    const e = {};
    if (!formData.name.trim()) e.name = t('accounts.accountName', 'Name') + ' is required';
    if (formData.initialBalance === '' || isNaN(Number(formData.initialBalance))) {
      e.initialBalance = t('accounts.initialBalance', 'Initial balance') + ' must be a number';
    }
    return e;
  };

  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setSaving(true);
    const isLiability = LIABILITY_TYPES.includes(formData.type);
    try {
      await onSave({
        name: formData.name.trim(),
        type: formData.type,
        initialBalance: Number(formData.initialBalance),
        currency: formData.currency || 'EUR',
        color: formData.color,
        icon: formData.icon || '🏦',
        institution: formData.institution.trim() || undefined,
        // Liability fields — only send when applicable
        interestRate:   isLiability && formData.interestRate !== '' ? Number(formData.interestRate) : null,
        minimumPayment: isLiability && formData.minimumPayment !== '' ? Number(formData.minimumPayment) : null,
        originalAmount: isLiability && formData.originalAmount !== '' ? Number(formData.originalAmount) : null,
        dueDate:        isLiability && formData.dueDate !== '' ? Number(formData.dueDate) : null,
      });
      onClose();
    } catch (err) {
      setErrors({ submit: err.response?.data?.message || err.message });
    } finally {
      setSaving(false);
    }
  };

  const QUICK_ICONS = ['🏦', '💳', '💰', '🏧', '📱', '🪙', '💵', '📈', '🏠', '✈️'];
  const isLiabilityType = LIABILITY_TYPES.includes(formData.type);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" fullScreen={isMobile}>
      <DialogTitle>
        {account ? t('accounts.editAccount', 'Edit Account') : t('accounts.addAccount', 'Add Account')}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          {errors.submit && <Alert severity="error">{errors.submit}</Alert>}

          <TextField
            fullWidth
            label={t('accounts.accountName', 'Account Name')}
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            error={!!errors.name}
            helperText={errors.name || ' '}
            autoFocus
          />

          <FormControl fullWidth>
            <InputLabel>{t('accounts.accountType', 'Account Type')}</InputLabel>
            <Select
              value={formData.type}
              label={t('accounts.accountType', 'Account Type')}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
            >
              {ACCOUNT_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {t(`accounts.types.${type}`, type)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            type="number"
            label={t('accounts.initialBalance', 'Initial Balance')}
            value={formData.initialBalance}
            onChange={(e) => setFormData({ ...formData, initialBalance: e.target.value })}
            error={!!errors.initialBalance}
            helperText={errors.initialBalance || ' '}
            InputProps={{
              inputProps: { step: '0.01' },
            }}
          />

          <TextField
            fullWidth
            label="Currency"
            value={formData.currency}
            onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
            InputProps={{
              startAdornment: <InputAdornment position="start">€</InputAdornment>,
            }}
          />

          <TextField
            fullWidth
            label={t('accounts.institution', 'Institution (optional)')}
            value={formData.institution}
            onChange={(e) => setFormData({ ...formData, institution: e.target.value })}
            helperText=" "
          />

          {/* Liability-specific fields */}
          {isLiabilityType && (
            <Box>
              <Typography variant="subtitle2" color="error.main" gutterBottom sx={{ mb: 1.5 }}>
                Liability Details
              </Typography>
              <Stack spacing={2}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Interest Rate (% APR)"
                    value={formData.interestRate}
                    onChange={(e) => setFormData({ ...formData, interestRate: e.target.value })}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                      inputProps: { step: '0.01', min: '0' },
                    }}
                    helperText="Annual percentage rate"
                  />
                  <TextField
                    fullWidth
                    type="number"
                    label="Minimum Payment"
                    value={formData.minimumPayment}
                    onChange={(e) => setFormData({ ...formData, minimumPayment: e.target.value })}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">€</InputAdornment>,
                      inputProps: { step: '0.01', min: '0' },
                    }}
                    helperText="Monthly minimum payment"
                  />
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Original Loan Amount"
                    value={formData.originalAmount}
                    onChange={(e) => setFormData({ ...formData, originalAmount: e.target.value })}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">€</InputAdornment>,
                      inputProps: { step: '0.01', min: '0' },
                    }}
                    helperText="Total amount originally borrowed"
                  />
                  <TextField
                    fullWidth
                    type="number"
                    label="Payment Due Day"
                    value={formData.dueDate}
                    onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                    InputProps={{
                      inputProps: { step: '1', min: '1', max: '31' },
                    }}
                    helperText="Day of month (1–31)"
                  />
                </Stack>
              </Stack>
            </Box>
          )}

          {/* Icon picker */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              {t('accounts.icon', 'Icon')}
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={1} mb={1}>
              {QUICK_ICONS.map((ic) => (
                <Box
                  key={ic}
                  onClick={() => setFormData({ ...formData, icon: ic })}
                  sx={{
                    width: 40, height: 40, fontSize: 22,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', borderRadius: 2, border: '1px solid',
                    borderColor: formData.icon === ic ? 'primary.main' : 'divider',
                    bgcolor: formData.icon === ic ? alpha(theme.palette.primary.main, 0.1) : 'background.paper',
                    transition: 'all 0.15s',
                    '&:hover': { transform: 'scale(1.1)' },
                  }}
                >
                  {ic}
                </Box>
              ))}
            </Stack>
            <TextField
              size="small"
              label="Custom icon / emoji"
              value={formData.icon}
              onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
              sx={{ maxWidth: 180 }}
            />
          </Box>

          {/* Colour */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              {t('accounts.color', 'Color')}
            </Typography>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box
                sx={{
                  width: 52, height: 52, borderRadius: 2,
                  bgcolor: formData.color, position: 'relative', overflow: 'hidden',
                  border: '2px solid', borderColor: 'divider',
                  flexShrink: 0,
                }}
              >
                <input
                  type="color"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }}
                />
              </Box>
              <Typography variant="body2" color="text.secondary">
                {formData.color} — click square to change
              </Typography>
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={saving}>
          {saving ? <CircularProgress size={18} /> : account ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─────────────────────────────────────────────────────
// DeleteConfirmDialog
// ─────────────────────────────────────────────────────
const DeleteConfirmDialog = ({ open, onClose, onConfirm, account, t }) => {
  const [deleting, setDeleting] = useState(false);

  const handleConfirm = async () => {
    setDeleting(true);
    try { await onConfirm(); onClose(); }
    finally { setDeleting(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('accounts.deleteAccount', 'Delete Account')}</DialogTitle>
      <DialogContent>
        <Typography>
          {t('accounts.deleteConfirm', 'Are you sure you want to delete this account?')}
          {account && <strong> "{account.name}"</strong>}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={deleting}>Cancel</Button>
        <Button onClick={handleConfirm} color="error" variant="contained" disabled={deleting}>
          {deleting ? <CircularProgress size={18} /> : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─────────────────────────────────────────────────────
// AccountsPage
// ─────────────────────────────────────────────────────
const AccountsPage = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { accounts, loading, error, limitReached, createAccount, updateAccount, deleteAccount, setAsDefaultAccount } = useAccount();

  const [serverSummary, setServerSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(null);
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

  // Try the server-side aggregated summary; if it fails or returns null we
  // fall back to a client-side derivation from the AccountContext data.
  useEffect(() => {
    const fetchSummary = async () => {
      setSummaryLoading(true);
      try {
        const api = createAuthApi();
        const res = await api.get('/accounts/summary');
        setServerSummary(res.data || null);
      } catch (_err) {
        // Non-blocking — keep server summary null and we'll derive locally.
        setServerSummary(null);
      } finally {
        setSummaryLoading(false);
      }
    };
    fetchSummary();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts]);

  // Derive totals from the shared AccountContext list. This is the
  // authoritative client-side fallback so the summary card is NEVER zero
  // (or missing) when accounts are clearly present in the list below.
  const derivedSummary = React.useMemo(() => {
    if (!Array.isArray(accounts) || accounts.length === 0) return null;
    let totalBalance = 0;
    let totalAllocated = 0;
    let totalAvailable = 0;
    for (const a of accounts) {
      const bal = a.currentBalance ?? a.initialBalance ?? 0;
      const alloc = a.allocated || 0;
      const avail = a.available != null ? a.available : (bal - alloc);
      totalBalance   += bal;
      totalAllocated += alloc;
      totalAvailable += avail;
    }
    return { totalBalance, totalAllocated, totalAvailable };
  }, [accounts]);

  // Prefer server summary, but only when it actually agrees that accounts
  // exist. If the server reports zero/empty while we DO have accounts in
  // context (e.g. summary endpoint failed silently or is stale), fall back.
  const summary = (
    serverSummary &&
    Array.isArray(accounts) && accounts.length > 0 &&
    (serverSummary.totalBalance !== 0 ||
     serverSummary.totalAllocated !== 0 ||
     (Array.isArray(serverSummary.accounts) && serverSummary.accounts.length === accounts.length))
  )
    ? serverSummary
    : derivedSummary;

  const handleSetDefault = async (id) => {
    await setAsDefaultAccount(id);
    setSnack({ open: true, msg: 'Default account updated', severity: 'success' });
  };

  const handleAdd = () => { setEditingAccount(null); setFormOpen(true); };
  const handleEdit = (account) => { setEditingAccount(account); setFormOpen(true); };
  const handleDeleteClick = (account) => { setDeletingAccount(account); setDeleteOpen(true); };

  const handleSave = async (data) => {
    if (editingAccount) {
      await updateAccount(editingAccount._id, data);
      setSnack({ open: true, msg: t('accounts.accountUpdated', 'Account updated'), severity: 'success' });
    } else {
      await createAccount(data);
      setSnack({ open: true, msg: t('accounts.accountCreated', 'Account created'), severity: 'success' });
    }
  };

  const handleDeleteConfirm = async () => {
    await deleteAccount(deletingAccount._id);
    setSnack({ open: true, msg: t('accounts.accountDeleted', 'Account deleted'), severity: 'info' });
  };

  const summaryCard = (label, value, color) => (
    <Box
      sx={{
        flex: 1,
        p: 2,
        borderRadius: 2,
        bgcolor: isDark ? alpha(color, 0.12) : alpha(color, 0.08),
        border: `1px solid ${alpha(color, 0.25)}`,
        minWidth: 0,
      }}
    >
      <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.06}>
        {label}
      </Typography>
      {summaryLoading ? (
        <CircularProgress size={16} sx={{ mt: 0.5 }} />
      ) : (
        <Typography variant="h6" fontWeight={800} color={color} sx={{ mt: 0.25 }}>
          {value != null ? value.toFixed(2) : '—'}
        </Typography>
      )}
    </Box>
  );

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1200, mx: 'auto' }}>
      {/* Page header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h5" fontWeight={700}>
          {t('accounts.myAccounts', 'My Accounts')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAdd}
          disabled={limitReached}
          sx={{ borderRadius: 2 }}
        >
          {t('accounts.addAccount', 'Add Account')}
        </Button>
      </Stack>

      {/* Limit warning */}
      {limitReached && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('accounts.limitReached', "You've reached your account limit. Upgrade your plan to add more accounts.")}
        </Alert>
      )}

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      {/* Summary bar */}
      {summary && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} mb={3}>
          {summaryCard(t('accounts.totalBalance', 'Total Balance'), summary.totalBalance, '#10b981')}
          {summaryCard(t('accounts.allocated', 'Allocated'), summary.totalAllocated, '#3b82f6')}
          {summaryCard(t('accounts.available', 'Available'), summary.totalAvailable, '#8b5cf6')}
        </Stack>
      )}

      {/* Multi-currency consolidated wallet */}
      <MultiCurrencyDashboard />

      {/* Content */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : accounts.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center', py: 10,
            border: '2px dashed', borderColor: 'divider',
            borderRadius: 3,
          }}
        >
          <AccountBalanceIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" mb={1}>
            {t('accounts.noAccounts', 'No accounts yet. Add your first account to track where your money lives.')}
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd} sx={{ mt: 1 }}>
            {t('accounts.addAccount', 'Add Account')}
          </Button>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {accounts.map((acc) => (
            <Grid item xs={12} sm={6} md={4} key={acc._id}>
              <AccountCard
                account={acc}
                onEdit={handleEdit}
                onDelete={handleDeleteClick}
                onSetDefault={handleSetDefault}
                t={t}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Add/Edit dialog */}
      <AccountFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSave={handleSave}
        account={editingAccount}
        t={t}
      />

      {/* Delete confirm dialog */}
      <DeleteConfirmDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDeleteConfirm}
        account={deletingAccount}
        t={t}
      />

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AccountsPage;
